import { useState, FormEvent, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Logo } from '../../components/ui/Logo';

const COUNTRIES = [
  { value: 'AR', label: 'Argentina' },
  { value: 'BO', label: 'Bolivia' },
  { value: 'BR', label: 'Brasil' },
  { value: 'CA', label: 'Canadá' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
  { value: 'CR', label: 'Costa Rica' },
  { value: 'CU', label: 'Cuba' },
  { value: 'DE', label: 'Alemania' },
  { value: 'DO', label: 'República Dominicana' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'ES', label: 'España' },
  { value: 'SV', label: 'El Salvador' },
  { value: 'FR', label: 'Francia' },
  { value: 'GB', label: 'Reino Unido' },
  { value: 'GT', label: 'Guatemala' },
  { value: 'HN', label: 'Honduras' },
  { value: 'IT', label: 'Italia' },
  { value: 'JP', label: 'Japón' },
  { value: 'MX', label: 'México' },
  { value: 'NI', label: 'Nicaragua' },
  { value: 'PA', label: 'Panamá' },
  { value: 'PE', label: 'Perú' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'PT', label: 'Portugal' },
  { value: 'PY', label: 'Paraguay' },
  { value: 'US', label: 'Estados Unidos' },
  { value: 'UY', label: 'Uruguay' },
  { value: 'VE', label: 'Venezuela' },
];

// Nickname validation status
type NicknameStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

function NicknameIndicator({ status }: { status: NicknameStatus }) {
  if (status === 'idle') return null;
  if (status === 'checking') return <span className="text-gray-400 text-xs font-semibold">Verificando...</span>;
  if (status === 'available') return <span className="text-green-600 text-xs font-bold flex items-center gap-1">✓ Disponible</span>;
  if (status === 'taken') return <span className="text-red-500 text-xs font-bold flex items-center gap-1">✗ Ya está en uso</span>;
  if (status === 'invalid') return <span className="text-red-500 text-xs font-bold">3-20 caracteres, letras, números y _</span>;
  return null;
}

export default function RegisterPage() {
  const { register, confirmRegistration } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<'register' | 'confirm'>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [pais, setPais] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [nicknameStatus, setNicknameStatus] = useState<NicknameStatus>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Validate nickname format and availability with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = username.trim();
    if (!trimmed || trimmed.length < 3) {
      setNicknameStatus(trimmed.length > 0 ? 'invalid' : 'idle');
      return;
    }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(trimmed)) {
      setNicknameStatus('invalid');
      return;
    }

    setNicknameStatus('checking');
    debounceRef.current = setTimeout(async () => {
      try {
        // Use the public users endpoint to check if nickname exists
        const apiUrl = import.meta.env.VITE_API_URL as string;
        const res = await fetch(`${apiUrl}/users/${trimmed}`);
        setNicknameStatus(res.ok ? 'taken' : 'available');
      } catch {
        // Network error or 404 = available
        setNicknameStatus('available');
      }
    }, 500);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [username]);

  function friendlyError(msg: string): string {
    if (msg.includes('User already exists') || msg.includes('already a registered') || msg.includes('already exists'))
      return 'Ya existe una cuenta con este correo electrónico.';
    if (msg.includes('ya está en uso'))
      return `El nickname "${username}" ya está en uso. Elige otro.`;
    if (msg.includes('nickname es obligatorio'))
      return 'El nickname es obligatorio.';
    if (msg.includes('entre 3 y 20'))
      return 'El nickname debe tener entre 3 y 20 caracteres (letras, números y _).';
    if (msg.includes('Password') || msg.includes('password'))
      return 'La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula y un número.';
    return msg;
  }

  async function handleRegister(e: FormEvent) {
    e.preventDefault();
    if (!pais) { setError('Selecciona tu país para continuar'); return; }
    setError('');
    setLoading(true);
    try {
      await register(email, password, username);
      setStep('confirm');
    } catch (err: unknown) {
      setError(friendlyError(err instanceof Error ? err.message : 'Error al registrarse'));
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmRegistration(email, code);
      // Guardar pais temporalmente para que ProfilePage lo persista al primer login
      if (pais) localStorage.setItem('pending_pais', pais);
      navigate('/login');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Código inválido');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{ background: 'radial-gradient(ellipse at 40% 80%, #0f172a 0%, #2d1b69 100%)' }}
    >
      {/* Floating decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        {['🎖️', '🌟', '⚽', '🏅', '✨', '🎯'].map((emoji, i) => (
          <span
            key={i}
            className="absolute text-3xl opacity-10"
            style={{
              top:       `${[10, 65, 40, 80, 25, 55][i]}%`,
              left:      `${[85, 10, 78, 90, 5,  50][i]}%`,
              transform: `rotate(${[10, -8, 20, -15, 5, -12][i]}deg)`,
            }}
          >
            {emoji}
          </span>
        ))}
      </div>

      <div className="relative w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-brand-600 to-brand-700 px-8 py-7 flex flex-col items-center gap-1">
            <Logo size="md" />
            <p className="text-white/60 text-xs font-body mt-1">
              {step === 'register' ? 'Crea tu colección gratis' : 'Casi listo ⚡'}
            </p>
          </div>

          {/* Form */}
          <div className="px-8 py-7">
            {step === 'register' ? (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-5">Crear cuenta</h2>
                <form onSubmit={handleRegister} className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                      Usuario
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="ej. ronaldinho10"
                        value={username}
                        onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
                        required
                        minLength={3}
                        maxLength={20}
                        className={[
                          'input-field pr-8',
                          nicknameStatus === 'available' ? 'border-green-400 focus:border-green-500' : '',
                          nicknameStatus === 'taken' || nicknameStatus === 'invalid' ? 'border-red-400 focus:border-red-500' : '',
                        ].join(' ')}
                      />
                      {/* Status icon */}
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {nicknameStatus === 'available' && <span className="text-green-500 text-lg">✓</span>}
                        {(nicknameStatus === 'taken' || nicknameStatus === 'invalid') && <span className="text-red-500 text-lg">✗</span>}
                        {nicknameStatus === 'checking' && <span className="text-gray-400 text-sm animate-spin">⏳</span>}
                      </div>
                    </div>
                    <div className="mt-1 h-4">
                      <NicknameIndicator status={nicknameStatus} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                      Correo electrónico
                    </label>
                    <input
                      type="email"
                      placeholder="tu@correo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                      Contraseña
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Mín. 8 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={8}
                        className="input-field pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                      🌎 País <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={pais}
                      onChange={(e) => setPais(e.target.value)}
                      required
                      className="input-field"
                    >
                      <option value="">-- Selecciona tu país --</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 font-semibold">
                      {error}
                    </div>
                  )}

                  <Button type="submit" loading={loading} size="lg" className="w-full mt-1"
                    disabled={nicknameStatus !== 'available'}>
                    Crear mi álbum
                  </Button>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Verificar email</h2>
                <p className="text-sm text-gray-500 mb-5">
                  Enviamos un código a <span className="font-bold text-gray-700">{email}</span>
                </p>
                <form onSubmit={handleConfirm} className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                      Código de verificación
                    </label>
                    <input
                      type="text"
                      placeholder="123456"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                      className="input-field text-center text-2xl tracking-[0.5em] font-bold"
                      maxLength={6}
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 font-semibold">
                      {error}
                    </div>
                  )}

                  <Button type="submit" loading={loading} size="lg" className="w-full mt-1">
                    Verificar y entrar
                  </Button>
                </form>
              </>
            )}

            <p className="mt-5 text-sm text-center text-gray-400">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-brand-500 font-bold hover:text-brand-600">
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
